import { useDate as getDateFormatter } from './useDate'

/**
 * Calculates the timezone offset string for a given date and timezone.
 * Accounts for daylight saving time.
 */
export const calculateTimezoneOffset = (date: Date, timezone: string): string => {
  // Use the passed date directly; do not construct another Date instance.
  // In our tests Date may be mocked and numeric overloads can behave unexpectedly.
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    hour12: false,
  })

  const hourPart = formatter.formatToParts(date).find((p) => p.type === 'hour')?.value
  const timezoneHour = hourPart ? parseInt(hourPart, 10) : NaN

  if (Number.isNaN(timezoneHour)) {
    return '+00:00'
  }

  const utcHour = date.getUTCHours()

  // We want the offset that should be *appended* to the wall-clock time we are
  // formatting.
  //
  // Let timezoneHour = local hour in the target timezone for the given instant.
  // Let utcHour      = UTC hour for the given instant.
  //
  // If local is behind UTC (e.g. LA: local 15, utc 23), the offset is negative:
  //   offsetHours = local - utc = -8  => "-08:00"
  // If local is ahead of UTC (e.g. Tokyo: local 08, utc 23), the offset is positive:
  //   offsetHours = +9          => "+09:00"
  let offsetHours = timezoneHour - utcHour

  // Normalize into a sensible range across day-boundaries.
  if (offsetHours > 12) offsetHours -= 24
  if (offsetHours < -12) offsetHours += 24

  // Ensure +00:00 (never -00:00)
  if (offsetHours === 0) return '+00:00'

  const offsetSign = offsetHours >= 0 ? '+' : '-'
  return `${offsetSign}${String(Math.abs(offsetHours)).padStart(2, '0')}:00`
}

/**
 * Formats a Date object to ISO8601 string with timezone offset.
 *
 * Produces a timestamp that uses the date's UTC clock fields combined with the
 * requested timezone's offset for that date.
 */
export const formatDateWithTimezone = (date: Date, timezone: string): string => {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  const seconds = String(date.getUTCSeconds()).padStart(2, '0')

  const offset = calculateTimezoneOffset(date, timezone)

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`
}

/**
 * Converts a UTC ISO8601 date string to local time ISO8601 with timezone offset
 * @param utcDateString - UTC date string (e.g., "2025-12-15T20:34:56Z")
 * @param timezone - IANA timezone string (e.g., "America/Los_Angeles")
 * @returns ISO8601 string in local time with offset (e.g., "2025-12-15T12:34:56-08:00")
 */
export const formatUtcToTimezoneOffset = (utcDateString: string, timezone: string): string => {
  const date = new Date(utcDateString)

  const formatter = getDateFormatter({
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const year = formatter.getPart(date, 'year')
  const month = formatter.getPart(date, 'month')
  const day = formatter.getPart(date, 'day')
  const hour = formatter.getPart(date, 'hour')
  const minute = formatter.getPart(date, 'minute')
  const second = formatter.getPart(date, 'second')
  const offset = calculateTimezoneOffset(date, timezone)

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${offset}`
}
